import { useRef, useState } from 'react'

import { v4 as uuidv4 } from 'uuid'

import { ANNOTATION_TYPE, CREATING_NEW_ANNOTATION_MODE } from '../utils/annotationUtils'


interface UseAnnotationCreationProps {
  creatingNewAnnotationMode: CREATING_NEW_ANNOTATION_MODE | null
  onStartCreatingNewAnnotation: () => void
  onFinishCreatingNewAnnotation: () => void
  onClearNewAnnotation: () => void
  onCreateNewAnnotation: (annotation: any) => void
  onAnnotationSelect: (annotation: any) => void
  selectedAnnotation: any
  isClickingMarker: boolean
}

export const useAnnotationCreation = ({
  creatingNewAnnotationMode,
  onStartCreatingNewAnnotation,
  onFinishCreatingNewAnnotation,
  onClearNewAnnotation,
  onCreateNewAnnotation,
  onAnnotationSelect,
  selectedAnnotation,
  isClickingMarker
}: UseAnnotationCreationProps) => {
  const isPainting = useRef(null)
  const currentShapeId = useRef(null)
  const [annotationInProgress, setAnnotationInProgress] = useState<any>(null)

  const onPointerDown = (e: any, stageRef: any) => {
    if (selectedAnnotation) {
      onAnnotationSelect(null)
      return
    }
    if (creatingNewAnnotationMode === null || isClickingMarker) return

    onStartCreatingNewAnnotation()
    const clickedOnMarker = e.target.getParent()?.className === 'Group'
    if (clickedOnMarker) {
      return
    }

    onClearNewAnnotation()
    const stage = stageRef.current
    const { x, y } = stage.getPointerPosition()

    currentShapeId.current = 0
    isPainting.current = true

    switch (creatingNewAnnotationMode) {
    case CREATING_NEW_ANNOTATION_MODE.RECTANGLE:
      const rectId = uuidv4()
      currentShapeId.current = rectId
      const newRect = {
        id: rectId,
        x,
        y,
        height: 1,
        width: 1,
        fillColor: "rgb(255,255,255)",
        type: ANNOTATION_TYPE.RECTANGLE
      }
      setAnnotationInProgress(newRect)
      break
    case CREATING_NEW_ANNOTATION_MODE.POINT:
      const pointId = uuidv4()
      currentShapeId.current = pointId
      const newPoint = {
        id: pointId,
        x,
        y,
        fillColor: "rgb(255,255,255)",
        type: ANNOTATION_TYPE.POINT
      }
      setAnnotationInProgress(newPoint)
      break
    }
  }

  const onPointerMove = (stageRef: any) => {
    if (creatingNewAnnotationMode === null || !isPainting.current) return

    const stage = stageRef.current
    const { x, y } = stage.getPointerPosition()

    switch (creatingNewAnnotationMode) {
    case CREATING_NEW_ANNOTATION_MODE.RECTANGLE:

      setAnnotationInProgress((annotation) => {
        if(!annotation) return annotation
        if (annotation && annotation.id === currentShapeId.current) {
          return {
            ...annotation,
            width: x - annotation.x,
            height: y - annotation.y,
          }
        }
        return annotation
      })
      break
    }
  }

  const onPointerUp = ({ onShowOverlay }) => {
    const isAnnotationPoint = (annotation: any) => {
      return Math.abs(annotation?.width) <= 4 && Math.abs(annotation?.height) <= 4
    }

    if (isAnnotationPoint(annotationInProgress)) {
      const newAnnotation = {
        ...annotationInProgress,
        type: ANNOTATION_TYPE.POINT,
        x: annotationInProgress.x + annotationInProgress.width / 2,
        y: annotationInProgress.y + annotationInProgress.height / 2,
      }
      finishCreatingNewAnnotation(newAnnotation)
    } else {
      finishCreatingNewAnnotation()
      onShowOverlay()
    }
  }

  const onMouseLeave = () => {
    finishCreatingNewAnnotation()
  }

  const finishCreatingNewAnnotation = (newAnnotation = null) => {
    const annotationToCreate = newAnnotation || annotationInProgress

    if (annotationToCreate && Object.keys(annotationToCreate).length > 0) {
      onCreateNewAnnotation(annotationToCreate)
      onFinishCreatingNewAnnotation()
    }

    isPainting.current = false
  }

  return {
    annotationInProgress,
    setAnnotationInProgress,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onMouseLeave
  }
}
